import React, { useState } from 'react';

import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';

import { AiField } from 'components/ai/field';
import { readPropertyEditFormContext } from 'components/ai/property_edit_form_context';

interface Props {
  aiEnabled?: boolean;
  feature: string;
  id: string;
  label: string;
  name: string;
  // The Property the copy is about, which the generators write from.
  propertyId: number;
  required?: boolean;
  // Left off so the textarea matches the form's others, which set no rows attribute either.
  rows?: number;
  // Absent on copy that describes the property itself rather than one of its two listings.
  side?: 'sale' | 'lease';
  tooltip?: string;
  value: string;
}

// A textarea on the property edit form that Otto can write. It renders the real form input and its
// label, so the surrounding simple_form wrapper and the form's own submit go on working untouched.
export default function AiTextarea({
  aiEnabled,
  feature,
  id,
  label,
  name,
  propertyId,
  required,
  rows,
  side,
  tooltip,
  value: saved,
}: Props) {
  const [value, setValue] = useState(saved || '');

  return (
    <AiField
      aiEnabled={aiEnabled}
      autoDraft
      context={() => ({ parentId: propertyId, side, ...readPropertyEditFormContext() })}
      feature={feature}
      value={value}
      onChange={setValue}
    >
      {({ className, generating }) => (
        // The form styles the label as an input-group addon, by a rule that wants it adjacent to the
        // input it squares off against.
        <div className="d-flex align-items-center">
          <label className={classNames('col-form-label text', required && 'required')} htmlFor={id}>
            {label}
            {tooltip && (
              <span className="float-end" data-placement="left" data-toggle="tooltip" title={tooltip}>
                <FontAwesomeIcon className="text-muted" icon={faInfoCircle} />
              </span>
            )}
            {required && <abbr title="required"> *</abbr>}
          </label>
          <textarea
            className={classNames('form-control', className)}
            id={id}
            name={name}
            readOnly={generating}
            rows={rows}
            value={value}
            onChange={event => setValue(event.target.value)}
          />
        </div>
      )}
    </AiField>
  );
}
